// /app/frontend/src/components/OrderTimeline.jsx
// Visual progress tracker showing order status history with timestamps

import React from 'react';
import { motion } from 'framer-motion';
import {
  Clock, Package, Truck, CheckCircle, XCircle
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Order Placed', icon: Clock, color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  processing: { label: 'Processing', icon: Package, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  shipped: { label: 'Shipped', icon: Truck, color: 'text-purple-600', bgColor: 'bg-purple-100' },
  delivered: { label: 'Delivered', icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' }
};

const STATUS_ORDER = ['pending', 'processing', 'shipped', 'delivered'];

const OrderTimeline = ({ 
  currentStatus = 'pending', 
  statusHistory = [],
  trackingNumber = null,
  carrier = null,
  compact = false 
}) => {
  // Determine which steps are complete based on current status
  const currentIndex = STATUS_ORDER.indexOf(currentStatus);
  
  // Build timeline from status history or generate default based on current status
  const getTimelineSteps = () => {
    if (statusHistory && statusHistory.length > 0) {
      // Use actual history
      return STATUS_ORDER.map(status => {
        const historyEntry = statusHistory.find(h => h.status === status);
        const statusIdx = STATUS_ORDER.indexOf(status);
        return {
          status,
          ...STATUS_CONFIG[status],
          timestamp: historyEntry?.timestamp,
          completed: statusIdx <= currentIndex,
          current: status === currentStatus,
          notes: historyEntry?.notes
        };
      });
    } else {
      // Generate default timeline based on current status
      return STATUS_ORDER.map((status, idx) => ({
        status,
        ...STATUS_CONFIG[status],
        timestamp: null,
        completed: idx <= currentIndex,
        current: status === currentStatus
      }));
    }
  };

  const steps = getTimelineSteps();
  
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (compact) {
    // Horizontal compact view
    return (
      <div className="flex items-center justify-between w-full" data-testid="order-timeline-compact">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          return (
            <React.Fragment key={step.status}>
              <div className="flex flex-col items-center">
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: step.current ? 1.1 : 1 }}
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    step.completed ? step.bgColor : 'bg-neutral-100'
                  } ${step.current ? 'ring-2 ring-offset-2 ring-amber-400' : ''}`}
                >
                  <Icon className={`w-4 h-4 ${step.completed ? step.color : 'text-neutral-400'}`} />
                </motion.div>
                <span className={`text-xs mt-1 ${step.completed ? 'text-neutral-700' : 'text-neutral-400'}`}>
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${
                  steps[idx + 1].completed ? 'bg-amber-400' : 'bg-neutral-200'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // Full vertical timeline view
  return (
    <div className="relative" data-testid="order-timeline">
      {/* Vertical line */}
      <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-neutral-200" />
      
      <div className="space-y-6">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={step.status}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="relative flex gap-4"
            >
              {/* Status icon */}
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: step.current ? 1.15 : 1 }}
                className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center ${
                  step.completed ? step.bgColor : 'bg-neutral-100 border border-neutral-200'
                } ${step.current ? 'ring-2 ring-offset-2 ring-amber-400' : ''}`}
              >
                <Icon className={`w-4 h-4 ${step.completed ? step.color : 'text-neutral-400'}`} />
              </motion.div>
              
              {/* Content */}
              <div className="flex-1 pb-4">
                <div className="flex items-center justify-between">
                  <h4 className={`font-medium ${step.completed ? 'text-neutral-900' : 'text-neutral-400'}`}>
                    {step.label}
                  </h4>
                  {step.timestamp && (
                    <span className="text-xs text-neutral-500">
                      {formatDate(step.timestamp)}
                    </span>
                  )}
                </div>
                
                {/* Tracking info for shipped status */}
                {step.status === 'shipped' && step.completed && trackingNumber && (
                  <div className="mt-2 p-2 bg-purple-50 rounded-lg text-sm">
                    <span className="text-purple-700">
                      <span className="font-medium">{carrier || 'USPS'}:</span> {trackingNumber}
                    </span>
                  </div>
                )}
                
                {/* Notes */}
                {step.notes && (
                  <p className="text-sm text-neutral-500 mt-1">{step.notes}</p>
                )}
                
                {/* Current status indicator */}
                {step.current && !step.timestamp && (
                  <span className="text-xs text-amber-600 mt-1 inline-block">Current status</span>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderTimeline;
